import { Controller } from "stimulus";
import { jitter } from "live_chat/backoff";
import { whenPlaybackStops, CROWD_REFRESH_SPREAD_MS } from "utils/live_playback";

// A go-live or schedule change broadcasts one Turbo page refresh to every /live page holding the
// podcast's "live_stream_settings" stream at the same moment: spread the re-fetches out rather than
// landing them all on the server in the same instant, and hold one while a video is still playing
// (see utils/live_playback). Only a refresh that arrived over the cable is touched; one a response
// renders (the follow gate's sign-in code step) is the viewer's own action and goes at once.
export default class RefreshSpreadController extends Controller {
  connect() {
    this.onCableMessage = (event) => this.noteCableMessage(event);
    this.onBeforeStreamRender = (event) => this.spread(event);
    // Capture: a cable stream source's message event does not bubble.
    document.addEventListener("message", this.onCableMessage, true);
    document.addEventListener("turbo:before-stream-render", this.onBeforeStreamRender);
  }

  disconnect() {
    document.removeEventListener("message", this.onCableMessage, true);
    document.removeEventListener("turbo:before-stream-render", this.onBeforeStreamRender);
    if (this.cancelPlaybackWait) this.cancelPlaybackWait();
    if (this.spreadTimer) window.clearTimeout(this.spreadTimer);
  }

  // Turbo renders a cable message synchronously inside its message event, so the mark only has to
  // outlive that one dispatch.
  noteCableMessage(event) {
    if (event.target.localName !== "turbo-cable-stream-source") return;

    this.fromCable = true;
    queueMicrotask(() => {
      this.fromCable = false;
    });
  }

  spread(event) {
    if (!this.fromCable || event.detail.newStream.action !== "refresh") return;
    // One refresh already waiting re-renders the whole page anyway.
    if (this.refreshPending) {
      event.preventDefault();
      return;
    }

    this.refreshPending = true;
    const render = event.detail.render;
    event.detail.render = (stream) =>
      new Promise((resolve) => {
        this.cancelPlaybackWait = whenPlaybackStops(() => {
          this.cancelPlaybackWait = null;
          this.spreadTimer = window.setTimeout(() => {
            this.spreadTimer = null;
            this.refreshPending = false;
            resolve(render(stream));
          }, jitter(0, CROWD_REFRESH_SPREAD_MS));
        });
      });
  }
}
